import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../../utils/api';

/**
 * Hook for detecting user idle state and managing review item heartbeats.
 *
 * Tracks mouse movement, keyboard input, clicks, and scroll events.
 * Sends periodic heartbeats while the user is active.
 * Calls onIdle when the user has been inactive for the configured timeout.
 *
 * @param {string|null} itemId - Currently assigned review item ID
 * @param {Function} onIdle - Called when idle timeout is reached
 */
export const useIdleDetection = (itemId, onIdle) => {
  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const timeoutSecondsRef = useRef(120);
  const [isIdle, setIsIdle] = useState(false);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  // Fetch the configured timeout from the server
  useEffect(() => {
    const fetchTimeout = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const response = await axios.get(`${apiBase}/admin/parsing-qa/idle-timeout`, { headers });
        const timeout = response.data?.review_idle_timeout_seconds;
        if (timeout && timeout > 0) {
          timeoutSecondsRef.current = timeout;
        }
      } catch (err) {
        // Use default 120s on error
      }
    };
    fetchTimeout();
  }, []);

  // Record activity
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (isIdle) {
      setIsIdle(false);
    }
  }, [isIdle]);

  // Send heartbeat to backend
  const sendHeartbeat = useCallback(async () => {
    if (!itemId) return;
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/parsing-qa/queue/${itemId}/heartbeat`, {}, { headers });
    } catch (err) {
      // Silently fail - item may have been unassigned
    }
  }, [itemId]);

  // Check if idle timeout has been reached
  const checkIdle = useCallback(() => {
    if (!itemId || timeoutSecondsRef.current <= 0) return;

    const elapsed = (Date.now() - lastActivityRef.current) / 1000;
    if (elapsed >= timeoutSecondsRef.current) {
      setIsIdle(true);
      onIdleRef.current?.();
    }
  }, [itemId]);

  // Set up activity listeners and timers
  useEffect(() => {
    if (!itemId || timeoutSecondsRef.current <= 0) return;

    // Reset idle state when item changes
    lastActivityRef.current = Date.now();
    setIsIdle(false);

    // Activity events to track
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, recordActivity, { passive: true }));

    // Also track visibility changes - going to another tab counts as idle
    const handleVisibility = () => {
      if (document.hidden) {
        // Don't reset activity when tab is hidden
      } else {
        recordActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Check for idle every 5 seconds
    idleTimerRef.current = setInterval(checkIdle, 5000);

    // Send heartbeat every 30 seconds while active
    // Send initial heartbeat immediately
    sendHeartbeat();
    heartbeatTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - lastActivityRef.current) / 1000;
      if (elapsed < timeoutSecondsRef.current) {
        sendHeartbeat();
      }
    }, 30000);

    return () => {
      events.forEach(event => document.removeEventListener(event, recordActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    };
  }, [itemId, recordActivity, checkIdle, sendHeartbeat]);

  return { isIdle };
};
